import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

export function GrowthReports() {
    const [activeTab, setActiveTab] = useState<'wiki' | 'meetings' | 'campaigns' | 'innovation'>('wiki');
    const [period, setPeriod] = useState('March 2026');

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Growth <span className="text-foreground">Reports</span></h1>
                    <p className="text-muted-foreground mt-1">Wiki Analytics · Meeting Insights · Campaign Performance · Innovation Metrics</p>
                </div>
                <div className="flex items-center gap-3">
                    <select value={period} onChange={e => setPeriod(e.target.value)}
                        className="bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none">
                        {['March 2026', 'February 2026', 'January 2026', 'Q1 2026'].map(p => <option key={p}>{p}</option>)}
                    </select>
                    <Button variant="secondary" onClick={() => toast.success('Exporting report...')}>Export PDF</Button>
                </div>
            </div>

            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'wiki', label: 'Wiki Analytics' },
                    { id: 'meetings', label: 'Meeting Insights' },
                    { id: 'campaigns', label: 'Campaign Performance' },
                    { id: 'innovation', label: 'Innovation Metrics' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative whitespace-nowrap ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {activeTab === 'wiki' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { label: 'Total Articles', value: '284', change: '+18', positive: true, color: 'text-primary' },
                            { label: 'Total Page Views', value: '42.8k', change: '+22%', positive: true, color: 'text-success' },
                            { label: 'Active Contributors', value: '34', change: '+5', positive: true, color: 'text-warning' },
                            { label: 'Searches', value: '1,240', change: '+8%', positive: true, color: 'text-foreground' },
                        ].map((s, i) => (
                            <Card key={i}><CardContent className="p-5">
                                <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                                <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                                <p className={`text-xs mt-1 font-semibold ${s.positive ? 'text-success' : 'text-destructive'}`}>{s.change} vs last month</p>
                            </CardContent></Card>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Most Viewed Articles</h2></CardHeader>
                            <CardContent className="p-0">
                                {[
                                    { title: 'Remote Work Policy 2026', views: 938, space: 'HR Policies', trend: '↑' },
                                    { title: 'Q1 2026 OKR Framework', views: 512, space: 'Company Wiki', trend: '↑' },
                                    { title: 'API Authentication Guide', views: 284, space: 'Engineering', trend: '→' },
                                    { title: 'Employee Handbook', views: 251, space: 'HR Policies', trend: '↑' },
                                    { title: 'Product Roadmap H1 2026', views: 43, space: 'Product Docs', trend: '↑' },
                                ].map((a, i) => (
                                    <div key={i} className="flex items-center justify-between px-6 py-3 border-b border-border">
                                        <div className="flex items-center gap-3">
                                            <span className="text-muted-foreground font-semibold text-sm w-5">#{i + 1}</span>
                                            <div>
                                                <p className="text-sm font-semibold text-foreground">{a.title}</p>
                                                <p className="text-xs text-muted-foreground">{a.space}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-semibold text-primary">{a.views.toLocaleString()}</p>
                                            <p className="text-xs text-muted-foreground">views {a.trend}</p>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Articles by Space</h2></CardHeader>
                            <CardContent className="p-6 space-y-3">
                                {[
                                    { space: 'Engineering', articles: 124, pct: 44, color: 'bg-info' },
                                    { space: 'Product Docs', articles: 67, pct: 24, color: 'bg-primary' },
                                    { space: 'Company Wiki', articles: 48, pct: 17, color: 'bg-primary' },
                                    { space: 'HR Policies', articles: 32, pct: 11, color: 'bg-success' },
                                    { space: 'Sales Playbook', articles: 13, pct: 4, color: 'bg-warning' },
                                ].map((s, i) => (
                                    <div key={i}>
                                        <div className="flex justify-between text-sm font-semibold mb-1">
                                            <span className="text-muted-foreground">{s.space}</span>
                                            <span className="text-foreground">{s.articles} ({s.pct}%)</span>
                                        </div>
                                        <div className="h-2 bg-background rounded-full overflow-hidden">
                                            <div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.pct}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {activeTab === 'meetings' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { label: 'Meetings Held', value: '68', color: 'text-primary' },
                            { label: 'Total Hours', value: '102h', color: 'text-warning' },
                            { label: 'Action Items Created', value: '124', color: 'text-success' },
                            { label: 'Transcribed Meetings', value: '41', color: 'text-info' },
                        ].map((s, i) => (
                            <Card key={i}><CardContent className="p-5">
                                <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                                <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                            </CardContent></Card>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Meetings by Type</h2></CardHeader>
                            <CardContent className="p-6 space-y-3">
                                {[
                                    { type: 'Video (Google Meet)', count: 32, pct: 47, color: 'bg-primary' },
                                    { type: 'Video (Zoom)', count: 21, pct: 31, color: 'bg-info' },
                                    { type: 'In-Person', count: 9, pct: 13, color: 'bg-success' },
                                    { type: 'Phone', count: 6, pct: 9, color: 'bg-warning' },
                                ].map((s, i) => (
                                    <div key={i}>
                                        <div className="flex justify-between text-sm font-semibold mb-1">
                                            <span className="text-muted-foreground">{s.type}</span>
                                            <span className="text-foreground">{s.count} ({s.pct}%)</span>
                                        </div>
                                        <div className="h-2 bg-background rounded-full overflow-hidden">
                                            <div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.pct}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Action Items Completion</h2></CardHeader>
                            <CardContent className="p-6 space-y-4">
                                <div className="text-center">
                                    <p className="text-6xl font-semibold text-success">74%</p>
                                    <p className="text-sm text-muted-foreground mt-1">Completion Rate</p>
                                </div>
                                <div className="space-y-2">
                                    {[
                                        { label: 'Completed', count: 92, color: 'bg-success' },
                                        { label: 'In Progress', count: 18, color: 'bg-primary' },
                                        { label: 'Overdue', count: 9, color: 'bg-destructive' },
                                        { label: 'Pending', count: 5, color: 'bg-text-600' },
                                    ].map((s, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${s.color}`} />
                                            <span className="text-xs text-muted-foreground flex-1">{s.label}</span>
                                            <span className="text-xs font-semibold text-foreground">{s.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {activeTab === 'campaigns' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { label: 'Campaigns Run', value: '5', color: 'text-primary' },
                            { label: 'Emails Delivered', value: '10.8k', color: 'text-success' },
                            { label: 'Avg Open Rate', value: '35%', color: 'text-warning' },
                            { label: 'Avg Click Rate', value: '21.4%', color: 'text-info' },
                        ].map((s, i) => (
                            <Card key={i}><CardContent className="p-5">
                                <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                                <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                            </CardContent></Card>
                        ))}
                    </div>
                    <Card>
                        <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Email Campaign Performance — {period}</h2></CardHeader>
                        <CardContent className="p-0 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                        <th className="text-left px-6 py-3">Campaign</th>
                                        <th className="text-center px-4 py-3">Sent</th>
                                        <th className="text-center px-4 py-3">Open Rate</th>
                                        <th className="text-center px-4 py-3">Click Rate</th>
                                        <th className="text-center px-4 py-3">Bounce</th>
                                        <th className="text-center px-4 py-3">Unsub</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border-12">
                                    {[
                                        { name: 'Monthly Newsletter — Feb', sent: 5200, open: 35.0, click: 21.4, bounce: 1.0, unsub: 0.3 },
                                        { name: 'Re-engagement Campaign', sent: 410, open: 22.0, click: 24.7, bounce: 1.5, unsub: 1.0 },
                                    ].map((c, i) => (
                                        <tr key={i} className="hover:bg-card/30">
                                            <td className="px-6 py-3 font-semibold text-foreground">{c.name}</td>
                                            <td className="px-4 py-3 text-center text-muted-foreground">{c.sent.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-center font-semibold text-success">{c.open}%</td>
                                            <td className="px-4 py-3 text-center font-semibold text-primary">{c.click}%</td>
                                            <td className="px-4 py-3 text-center text-destructive">{c.bounce}%</td>
                                            <td className="px-4 py-3 text-center text-muted-foreground">{c.unsub}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'innovation' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { label: 'Ideas Submitted', value: '47', color: 'text-primary' },
                            { label: 'Implemented', value: '1', color: 'text-success' },
                            { label: 'Actual ROI Delivered', value: '$145k', color: 'text-success' },
                            { label: 'Avg Votes per Idea', value: '68', color: 'text-warning' },
                        ].map((s, i) => (
                            <Card key={i}><CardContent className="p-5">
                                <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                                <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                            </CardContent></Card>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Ideas by Status</h2></CardHeader>
                            <CardContent className="p-6 space-y-3">
                                {[
                                    { status: 'Submitted', count: 20, pct: 42, color: 'bg-text-600' },
                                    { status: 'Under Review', count: 12, pct: 26, color: 'bg-warning' },
                                    { status: 'Approved', count: 8, pct: 17, color: 'bg-success' },
                                    { status: 'In Progress', count: 5, pct: 11, color: 'bg-primary' },
                                    { status: 'Implemented', count: 2, pct: 4, color: 'bg-success' },
                                ].map((s, i) => (
                                    <div key={i}>
                                        <div className="flex justify-between text-sm font-semibold mb-1">
                                            <span className="text-muted-foreground">{s.status}</span>
                                            <span className="text-foreground">{s.count} ({s.pct}%)</span>
                                        </div>
                                        <div className="h-2 bg-background rounded-full overflow-hidden">
                                            <div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.pct}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Innovation by Category</h2></CardHeader>
                            <CardContent className="p-6 space-y-3">
                                {[
                                    { cat: 'Technology', count: 19, pct: 40 },
                                    { cat: 'Culture', count: 14, pct: 30 },
                                    { cat: 'Process', count: 8, pct: 17 },
                                    { cat: 'Product', count: 4, pct: 8 },
                                    { cat: 'Other', count: 2, pct: 5 },
                                ].map((s, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">{s.cat}</span>
                                        <div className="flex items-center gap-2">
                                            <div className="w-20 h-1.5 bg-background rounded-full overflow-hidden">
                                                <div className="h-full bg-primary rounded-full" style={{ width: `${s.pct}%` }} />
                                            </div>
                                            <span className="text-foreground font-semibold w-6">{s.count}</span>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
